<div>
  <!-- STATE -->
  @if (loading) {
    <div class="text-sm text-gray-500">{{ 'DISB_NOTE.LOADING_COVENANT_COMPLIANCE' | translate }}</div>
  }

  @if (loadError && !loading) {
    <div class="text-sm text-red-600 mb-3">{{ loadError | translate }}</div>
  }

  @if (!loading) {
    @if (rows.length === 0) {
      <div class="text-sm text-gray-400">{{ emptyMessage | translate }}</div>
    } @else {
      <!-- PROGRESS -->
      <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
        <span
          class="text-xs font-medium px-2 py-1 rounded-full"
          [ngClass]="allAnswered ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'"
        >
          {{ 'DISB_NOTE.ANSWERED_COUNT' | translate: { answered: answeredCount, total: rows.length } }}
        </span>

        @if (orphanCount) {
          <span class="text-xs text-amber-700">
            {{ 'DISB_NOTE.ORPHAN_ANSWERS' | translate: { count: orphanCount } }}
          </span>
        }
      </div>

      <!-- GRID -->
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-start text-gray-600 border-b border-gray-200">
              <th class="py-2 pe-4 w-14">{{ 'DISB_NOTE.S_NO' | translate }}</th>
              <th class="py-2 pe-4">{{ 'COMMON.DESCRIPTION' | translate }}</th>
              <th class="py-2 pe-4 w-52">{{ 'DISB_NOTE.COMPLIANCE_STATUS' | translate }}</th>
              <th class="py-2 w-72" [class.pe-4]="mode === 'covenant'">{{ 'COMMON.REMARKS' | translate }}</th>

              @if (mode === 'covenant') {
                @if (showComment) {
                  <th class="py-2 pe-4 w-72">{{ 'DISB_NOTE.COMMENT' | translate }}</th>
                }
                <th class="py-2 w-36">{{ 'DISB_NOTE.UPDATE_DATE' | translate }}</th>
              }
            </tr>
          </thead>

          <tbody>
            @for (row of rows; track row.qstSeq; let i = $index) {
              <tr class="border-b border-gray-100 align-top">
                <td class="py-3 pe-4 text-gray-500">{{ i + 1 }}</td>

                <!-- Covenants carry multi-line text (e.g. the a/b/c tranche list). -->
                <td class="py-3 pe-4 text-gray-800 whitespace-pre-line">{{ row.question }}</td>

                <td class="py-3 pe-4">
                  <!-- Covenant status is answered on the credit proposal: view only here. -->
                  @if (mode === 'covenant') {
                    <div class="min-h-10 py-2.5 text-center text-gray-800 whitespace-pre-line wrap-break-word">
                      {{ complianceStatus(row) || '-' }}
                    </div>
                  } @else if (row.inputType === 'text') {
                    <input
                      type="text"
                      [(ngModel)]="row.ansVal"
                      [disabled]="readOnly"
                      class="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm outline-none focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/10"
                      [placeholder]="'COMMON.STATUS' | translate"
                    />
                  } @else {
                    <select
                      [(ngModel)]="row.ansVal"
                      [disabled]="readOnly"
                      class="w-full h-10 px-2 border rounded-lg bg-white text-sm outline-none focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/10"
                      [ngClass]="
                        isOrphanValue(row)
                          ? 'border-amber-300'
                          : row.ansVal
                            ? 'border-gray-200'
                            : 'border-gray-200 text-gray-400'
                      "
                    >
                      <option value="">{{ 'COMMON.SELECT' | translate }}</option>

                      @for (option of row.options; track option.answrSeq) {
                        <option [value]="option.answrStr">
                          {{ option.answrStr }}{{ option.isOrphan ? ' ' + ('DISB_NOTE.NOT_IN_SETUP' | translate) : '' }}
                        </option>
                      }
                    </select>
                  }
                </td>

                <td class="py-3" [class.pe-4]="mode === 'covenant'">
                  <!-- Covenant remarks come from the credit proposal: view only here. -->
                  @if (mode === 'covenant') {
                    <div class="min-h-10 py-2.5 text-center text-gray-800 whitespace-pre-line break-words">
                      {{ row.rmrks || '-' }}
                    </div>
                  } @else {
                    <input
                      type="text"
                      [(ngModel)]="row.rmrks"
                      [disabled]="readOnly"
                      class="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm outline-none focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/10"
                      [placeholder]="'COMMON.REMARKS' | translate"
                    />
                  }
                </td>

                @if (mode === 'covenant') {
                  @if (showComment) {
                  <td class="py-3 pe-4">
                    <input
                      type="text"
                      [(ngModel)]="row.cmnt"
                      [disabled]="readOnly"
                      class="w-full h-10 px-3 border border-gray-200 rounded-lg bg-white text-sm outline-none focus:border-[#1E2A78] focus:ring-2 focus:ring-[#1E2A78]/10"
                      [placeholder]="'DISB_NOTE.COMMENT' | translate"
                    />
                  </td>
                  }

                  <td class="py-3">
                    <div class="min-h-10 py-2.5 text-center text-gray-800 whitespace-nowrap">
                      {{ row.lastUpdDt ? (row.lastUpdDt | date: 'dd-MMM-yyyy') : '-' }}
                    </div>
                  </td>
                }
              </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- SAVE -->
      @if (showSaveButton && !readOnly) {
        <div class="flex justify-end pt-4">
        <button
          type="button"
          class="px-6 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          [disabled]="saving"
          (click)="save()"
        >
          {{ (saving ? 'COMMON.SAVING' : 'DISB_NOTE.SAVE_COVENANT_COMPLIANCE') | translate }}
          </button>
        </div>
      }
    }
  }
</div>
